import { SystemMessage, SystemMessageType, generateSystemMessage } from './system';
import { AuthMessage, AuthMessageType, generateAuthMessage } from './auth';
export type { AttestationConnectProof, AttestationConnectProofPayload, AuthChallengePayload } from './auth';
export {
  attestationConnectProofSchema,
  attestationConnectProofPayloadSchema,
  canonicalJson,
} from './auth';
import { ConnectionMessage, ConnectionMessageType, generateConnectionMessage } from './connection';
import { InvitationMessage, InvitationMessageType, generateInvitationMessage } from './invitation';
import { PresenceMessage, PresenceMessageType, generatePresenceMessage } from './presence';
import { PeerMessage, PeerMessageType, generatePeerMessage } from './peer';
import { HostMessage, HostMessageType, generateHostMessage } from './host';
import { GameSaveStorageMessageType } from './gameSaveStorage';
import { generateLobbyMessage, LobbyMessage, LobbyMessageType } from './lobby';
import { generateMetricMessage, MetricMessageType, MetricsMessage } from './metrics';
import { generateOutboundMessage, OutboundMessage, OutboundMessageType } from './outbound';
import { generateProfileMessage, ProfileMessage, ProfileMessageType } from './profile';
import { generateMatchmakingMessage, MatchmakingMessage, MatchmakingMessageType } from './matchmaking';
import { generateUserEventMessage, UserEventMessage, UserEventMessageType } from './userEvents';
import { AnalyticsMessage, AnalyticsMessageType, generateAnalyticsMessage } from './telemetryMessages';
import { generateNotificationMessage, NotificationMessage, NotificationMessageType } from './notification';
import { AdminMessage, AdminMessageType, generateAdminMessage } from './admin';
import { VpnMessage, VpnMessageType, generateVpnMessage } from './vpn';
import { BeaconMessage, BeaconMessageType, generateBeaconMessage } from './beacon';
import { GamingScoreMessage, GamingScoreMessageType, generators as gamingScoreGenerators } from './gamingScore';
import { ProvisioningMessage, ProvisioningMessageType, generateProvisioningMessage } from './provisioning';
import { MacroMessage, MacroMessageType, generateMacroMessage } from './macroWalk';
import { LiveOpsMessage, LiveOpsMessageType, generators as liveOpsGenerators } from './liveops';
import { UserProfileMessage, UserProfileMessageType, generateUserProfileMessage } from './userProfile';
import {
  NotificationInboxMessage,
  NotificationInboxMessageType,
  generateNotificationInboxMessage,
} from './notificationInbox';

// Combine all message types
export const WebsocketMessageType = {
  ...SystemMessageType,
  ...AuthMessageType,
  ...ConnectionMessageType,
  ...InvitationMessageType,
  ...LobbyMessageType,
  ...MetricMessageType,
  ...PresenceMessageType,
  ...PeerMessageType,
  ...HostMessageType,
  ...GameSaveStorageMessageType,
  ...OutboundMessageType,
  ...ProfileMessageType,
  ...MatchmakingMessageType,
  ...UserEventMessageType,
  ...AnalyticsMessageType,
  ...NotificationMessageType,
  ...AdminMessageType,
  ...VpnMessageType,
  ...BeaconMessageType,
  ...GamingScoreMessageType,
  ...ProvisioningMessageType,
  ...MacroMessageType,
  ...LiveOpsMessageType,
  ...UserProfileMessageType,
  ...NotificationInboxMessageType,
} as const;

export type WebsocketMessageType = (typeof WebsocketMessageType)[keyof typeof WebsocketMessageType];

// Combined message type
export type WebsocketMessage =
  | AuthMessage
  | ConnectionMessage
  | HostMessage
  | InvitationMessage
  | MetricsMessage
  | LobbyMessage
  | PeerMessage
  | PresenceMessage
  | SystemMessage
  | OutboundMessage
  | ProfileMessage
  | MatchmakingMessage
  | UserEventMessage
  | AnalyticsMessage
  | NotificationMessage
  | AdminMessage
  | VpnMessage
  | BeaconMessage
  | GamingScoreMessage
  | ProvisioningMessage
  | MacroMessage
  | LiveOpsMessage
  | UserProfileMessage
  | NotificationInboxMessage;

// Helper to get the payload type for any message type
export type PayloadForMessageType<T extends WebsocketMessageType> = T extends SystemMessageType
  ? Parameters<typeof generateSystemMessage>[1]
  : T extends AuthMessageType
    ? Parameters<typeof generateAuthMessage>[1]
    : T extends ConnectionMessageType
      ? Parameters<typeof generateConnectionMessage>[1]
      : T extends InvitationMessageType
        ? Parameters<typeof generateInvitationMessage>[1]
        : T extends LobbyMessageType
          ? Parameters<typeof generateLobbyMessage>[1]
          : T extends MetricMessageType
            ? Parameters<typeof generateMetricMessage>[1]
            : T extends PresenceMessageType
              ? Parameters<typeof generatePresenceMessage>[1]
              : T extends PeerMessageType
                ? Parameters<typeof generatePeerMessage>[1]
                : T extends OutboundMessageType
                  ? Parameters<typeof generateOutboundMessage>[1]
                  : T extends HostMessageType
                    ? Parameters<typeof generateHostMessage>[1]
                    : T extends ProfileMessageType
                      ? Parameters<typeof generateProfileMessage>[1]
                      : T extends MatchmakingMessageType
                        ? Parameters<typeof generateMatchmakingMessage>[1]
                        : T extends UserEventMessageType
                          ? Parameters<typeof generateUserEventMessage>[1]
                          : T extends AnalyticsMessageType
                            ? Parameters<typeof generateAnalyticsMessage>[1]
                            : T extends NotificationMessageType
                              ? Parameters<typeof generateNotificationMessage>[1]
                              : T extends AdminMessageType
                                ? Parameters<typeof generateAdminMessage>[1]
                                : T extends VpnMessageType
                                  ? Parameters<typeof generateVpnMessage>[1]
                                  : T extends BeaconMessageType
                                    ? Parameters<typeof generateBeaconMessage>[1]
                                    : T extends GamingScoreMessageType
                                      ? Parameters<typeof gamingScoreGenerators.submitScoringMetrics>[0]
                                      : T extends ProvisioningMessageType
                                        ? Parameters<typeof generateProvisioningMessage>[1]
                                        : T extends MacroMessageType
                                          ? Parameters<typeof generateMacroMessage>[1]
                                            : T extends LiveOpsMessageType
                                            ? Parameters<typeof liveOpsGenerators.liveOpsEvent>[0]
                                            : T extends UserProfileMessageType
                                              ? Parameters<typeof generateUserProfileMessage>[1]
                                              : T extends NotificationInboxMessageType
                                                ? Parameters<typeof generateNotificationInboxMessage>[0]
                                                : never;

export * from './shared';
export * from './system';
export * from './auth';
export * from './connection';
export * from './invitation';
export * from './lobby';
export * from './metrics';
export * from './presence';
export * from './peer';
export * from './host';
export * from './outbound';
export * from './profile';
export * from './matchmaking';
export * from './userEvents';
export * from './telemetryMessages';
export * from './notification';
export * from './vpn';
export * from './admin';
export * from './beacon';
export * from './gamingScore';
export * from './provisioning';
export * from './macroWalk';
export * from './userProfile';
export * from './notificationInbox';
// Explicit re-export (NOT `export *`) so liveops's internal `generators` /
// `validators` names don't collide with the same names already star-exported
// from `./gamingScore` (TS2308). Those are consumed via the `websocket`
// namespace; only the public contract symbols are surfaced here.
export {
  LiveOpsMessageType,
  LiveOpsClientEventName,
  liveOpsEventPayloadSchema,
  liveOpsMessageUnion,
  LIVEOPS_FUNNEL_STAGES,
  LIVEOPS_ERROR_KEYS,
  MAX_CONNECTION_DURATION_MS,
} from './liveops';
export type {
  LiveOpsEventPayload,
  LiveOpsMessage,
  LiveOpsFunnelStage,
  LiveOpsErrorKey,
} from './liveops';
